import React, { forwardRef } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import styled from 'styled-components';
import { colors, H3 } from '../../core/Styles';

export const List = styled.div`
  margin-top: 16px;
  margin-left: -23px;
  margin-right: -16px;
`;

export const ListHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.faintGrey};
  margin-left: 23px;
  margin-right: 16px;
  & > :last-child {
    padding-right: 14px;
  }
`;

export const ListHeaderItem = styled.div<{ span: number }>`
  flex: ${props => props.span};
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.grey};
  padding: 12px 0;
`;

export const ListBody = styled.div`
  overflow-y: auto;
  max-height: 65vh;
`;

export const ListItem = styled.div`
  display: flex;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: ${colors.normal};
  padding: 12px 16px 12px 23px;
  & > :last-child {
    padding-right: 14px;
  }
  &:hover {
    background-color: ${colors.background};
    transition: background-color 0.2s ease;
  }
  & > :not(:last-child) {
    margin-right: 16px;
  }
`;

export const ListItemColumn = styled.div<{ span: number }>`
  display: flex;
  flex: ${props => props.span};
  align-items: center;
  & > :not(:last-child) {
    margin-right: 12px;
  }
  & > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  min-width: 0;
`;

export const ListItemHorizontalDivider = styled.div`
  height: 1px;
  background-color: ${colors.faintGrey};
  margin-left: 23px;
  margin-right: 16px;
`;

export const EmptyStateDiv = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
`;

export const ChurchGroup = styled.div`
  margin-bottom: 32px;
  & > ${H3} {
    margin-bottom: 16px;
    color: ${colors.primary};
  }
`;

export const ChurchTotal = styled.div`
  padding: 16px 23px;
  border-top: 2px solid ${colors.faintGrey};
  margin-top: 8px;
  text-align: right;
`;

export const TotalSection = styled.div`
  padding: 16px 23px;
  border-top: 2px solid ${colors.faintGrey};
  margin-top: 8px;
  text-align: right;
`;

export const StatusBadge = styled.span<{ status?: string }>`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => {
    const status = props.status?.toLowerCase() || 'processing';
    // Completed / Success - Green
    if (status === 'completed' || status === 'success') {
      return '#10b981';
    }
    // Processing / Pending - Yellow
    if (status === 'pending' || status === 'processing' || status === 'in-progress') {
      return '#f59e0b';
    }
    // Failed / Cancelled - Red
    if (status === 'failed' || status === 'cancelled' || status === 'error') {
      return '#ef4444';
    }
    return '#f59e0b'; // Default to Processing (yellow)
  }};
  color: #ffffff;
  text-transform: capitalize;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

export const AmountText = styled.span<{ color: string }>`
  font-weight: 700;
  color: ${props => props.color};
  font-size: 14px;
`;

export const BackButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: ${colors.primary};
  font-weight: 600;
  &:hover {
    opacity: 0.8;
  }
`;

export const ChurchNameHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.faintGrey};
`;

export const ChurchListItem = styled(ListItem)`
  cursor: pointer;
  border-radius: 4px;
  margin: 2px 0;
  position: relative;
  transition: all 0.2s ease;
  &:hover {
    background-color: ${colors.background};
    transform: translateX(4px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    border-left: 3px solid ${colors.primary};
    padding-left: 20px;
  }
`;

export const ChurchListItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.grey};
  opacity: 0;
  transition: all 0.2s ease;
  margin-left: auto;
  ${ChurchListItem}:hover & {
    opacity: 1;
    color: ${colors.primary};
    transform: translateX(2px);
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  margin-top: 24px;
  border-top: 2px solid ${colors.faintGrey};
  flex-wrap: wrap;
  background-color: ${colors.background};
  border-radius: 0 0 8px 8px;
`;

export const PaginationButton = styled.button<{ disabled?: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${props => (props.disabled ? colors.grey : colors.primary)};
  background-color: ${props => (props.disabled ? colors.faintGrey : '#ffffff')};
  border: 1px solid ${props => (props.disabled ? colors.faintGrey : colors.faintGrey)};
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover:not(:disabled) {
    background-color: ${colors.primary};
    color: #ffffff;
    border-color: ${colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PageNumbers = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const PageNumberButton = styled.button<{ active?: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: ${props => (props.active ? '700' : '600')};
  font-size: 14px;
  color: ${props => (props.active ? '#ffffff' : colors.normal)};
  background-color: ${props => (props.active ? colors.primary : '#ffffff')};
  border: 1px solid ${props => (props.active ? colors.primary : colors.faintGrey)};
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover {
    background-color: ${props => (props.active ? colors.primary : colors.primary)};
    color: #ffffff;
    border-color: ${colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const Ellipsis = styled.span`
  padding: 0 4px;
  color: ${colors.grey};
  font-weight: 600;
`;

export const PageInfo = styled.div`
  min-width: 100px;
  text-align: center;
  margin-left: 12px;
  padding: 8px 16px;
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid ${colors.faintGrey};
  font-weight: 600;
`;

export const SearchContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

export const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 20px;
  padding: 20px;
  background-color: ${colors.background};
  border-radius: 12px;
  border: 1px solid ${colors.faintGrey};
`;

export const StatusFilterWrapper = styled.div`
  min-width: 140px;
`;

export const DateRangeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
`;

export const DateFilterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${colors.faintGrey};
  border-radius: 8px;
  background-color: #ffffff;
  padding: 0 12px;
  min-width: 130px;
  height: 40px;
  transition: all 0.2s ease;

  svg {
    flex-shrink: 0;
    color: ${colors.grey};
  }

  &:hover {
    border-color: ${colors.primary};
  }
  &:focus-within {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
`;

export const DateSeparator = styled.span`
  font-weight: 500;
  font-size: 12px;
  color: ${colors.grey};
  padding: 0 4px;
  flex-shrink: 0;
`;

export const ClearDateIconButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: ${colors.faintGrey};
  color: ${colors.grey};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background-color: ${colors.grey};
    color: #ffffff;
    transform: scale(1.05);
  }
`;

export const DateInputButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  color: ${colors.normal};
  flex: 1;
  min-width: 90px;
  text-align: left;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CustomDateInput = forwardRef<HTMLButtonElement, any>((props: any, ref: any) => {
  return (
    <DateInputButton ref={ref} onClick={props.onClick} disabled={props.disabled}>
      {props.value || props.placeholder}
      <FiChevronDown size={16} />
    </DateInputButton>
  );
});

export const ClearDateButton = styled.button`
  all: unset;
  padding: 10px 16px;
  border-radius: 8px;
  background-color: ${colors.faintGrey};
  color: ${colors.normal};
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: ${colors.grey};
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

// DatePicker styling
export const DatePickerStyles = styled.div`
  .react-datepicker {
    font-family: 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell',
      'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    background: #ffffff;
    box-shadow: 0px 0px 5px 1px rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    border: none;
  }
  .react-datepicker__header {
    background: #ffffff;
    border-bottom: none;
  }
  .react-datepicker__current-month {
    font-weight: bold;
    font-size: 16px;
    letter-spacing: 0.035em;
    margin-bottom: 8px;
  }
  .react-datepicker__day-names {
    text-transform: uppercase;
    font-weight: bold;
    font-size: 10px;
    letter-spacing: 0.035em;
  }
  .react-datepicker__day--outside-month {
    color: rgba(0, 0, 0, 0.5);
  }
  .react-datepicker__day--selected {
    background: ${colors.primary};
  }
  .react-datepicker__day--disabled {
    color: ${colors.faintGrey};
    cursor: not-allowed;
  }
`;
